import { useState, useRef, useEffect } from 'react';
import { aiBibleDetection } from '../services/groqAi';
import { parseBibleReference } from '../lib/bibleReference';
import { toast } from '../lib/toast';

// Dedupe by reference, keep the ten most recent.
const mergeDetected = (prev, found) => {
  const unique = [];
  [...prev, ...found].forEach(p => {
    if (!unique.some(u => u.reference === p.reference)) unique.push(p);
  });
  return unique.slice(-10);
};

// Live scripture detection from the preacher's voice.
//
// Listens with the browser's SpeechRecognition, looks for Bible references in
// the interim (early) and final transcript, and asks SleekHear AI to spot
// quoted verses that were not named. Whatever it finds is handed to
// `displayPassageByReference`, which stages it.
//
// `isListeningRef` is created by the caller and shared with useMicrophone.
export default function useVoiceDetection({
  addDebugLog,
  logActivity,
  displayPassageByReference,
  isListeningRef,
  startAudioMonitoring,
  stopAudioMonitoring,
}) {
  const [isListening, setIsListening] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [detectedPassages, setDetectedPassages] = useState([]);

  const recognitionRef = useRef(null);
  const isRecognizingRef = useRef(false); // true while SpeechRecognition is actively running
  const transcriptBuffer = useRef('');
  const lastInterimDetectedRef = useRef('');
  const stagedPassageRef = useRef(null); // { book, chapter, verseStart, verseEnd } of what's currently staged
  // The recognition handlers are created once; these always point at this
  // render's versions of the functions below.
  const detectBiblePassagesRef = useRef(null);
  const displayPassageByReferenceRef = useRef(null);
  const detectPassagesWithGroqRef = useRef(null);

  const detectBiblePassages = (text) => {
    addDebugLog(`🔍 Scanning: "${text}"`);
    const words = text.toLowerCase().split(/\s+/);
    const passages = [];

    for (let i = 0; i < words.length; i++) {
      let bestMatch = null;
      let bestLen = 0;

      for (let len = 1; len <= Math.min(10, words.length - i); len++) {
        const phrase = words.slice(i, i + len).join(' ');
        const parsed = parseBibleReference(phrase);
        if (parsed) {
          if (!bestMatch || parsed.verseStart !== null || len > bestLen) {
            bestMatch = parsed;
            bestLen = len;
          }
        }
      }

      if (bestMatch) {
        const isDuplicate = passages.some(p => p.reference === bestMatch.reference);
        if (!isDuplicate) {
          passages.push(bestMatch);
          addDebugLog(`✨ Detected: ${bestMatch.reference}`);
        }
        i += bestLen - 1;
      }
    }

    if (passages.length > 0) {
      setDetectedPassages(prev => mergeDetected(prev, passages));

      let lastPassage = passages[passages.length - 1];

      // If the newly detected passage continues the same book/chapter
      // already staged (e.g. reader kept going verse-by-verse without
      // saying "verse" or "through" each time), extend the range instead
      // of replacing the display with just the new single verse.
      const staged = stagedPassageRef.current;
      if (
        staged &&
        lastPassage.book === staged.book &&
        lastPassage.chapter === staged.chapter &&
        lastPassage.verseStart !== null &&
        staged.verseEnd !== null &&
        lastPassage.verseStart >= staged.verseStart &&
        lastPassage.verseStart <= staged.verseEnd + 3 // allow a small gap for missed words
      ) {
        const mergedEnd = Math.max(staged.verseEnd, lastPassage.verseEnd ?? lastPassage.verseStart);
        lastPassage = {
          book: staged.book,
          chapter: staged.chapter,
          verseStart: staged.verseStart,
          verseEnd: mergedEnd,
          reference: `${staged.book} ${staged.chapter}:${staged.verseStart}-${mergedEnd}`
        };
      }

      addDebugLog(`📺 Displaying: ${lastPassage.reference}`);
      stagedPassageRef.current = lastPassage;
      displayPassageByReference(lastPassage.reference);
    } else {
      addDebugLog(`❌ No passages detected in: "${text}"`);
    }
  };

  const detectPassagesWithGroq = async (text) => {
    try {
      addDebugLog(`🤖 SleekHear AI analyzing: "${text.trim().substring(0, 60)}..."`);
      const aiResults = await aiBibleDetection(text);
      addDebugLog(`🤖 SleekHear AI returned ${aiResults.length} result(s)`);
      if (aiResults.length > 0) {
        const newPassages = aiResults
          .filter(r => {
            if (r.confidence < 0.7) {
              addDebugLog(`🤖 SleekHear AI: Skipped "${r.reference}" (confidence: ${r.confidence})`);
              return false;
            }
            return true;
          })
          .map(r => {
            const parsed = parseBibleReference(r.reference);
            return parsed || { reference: r.reference, book: r.reference, chapter: null, verseStart: null, verseEnd: null };
          });

        if (newPassages.length > 0) {
          setDetectedPassages(prev => mergeDetected(prev, newPassages));
          addDebugLog(`🤖 SleekHear AI detected ${newPassages.length} additional verse(s)`);

          // Display the last detected passage
          const lastPassage = newPassages[newPassages.length - 1];
          addDebugLog(`📺 SleekHear AI displaying: ${lastPassage.reference}`);
          displayPassageByReference(lastPassage.reference);
        }
      } else {
        addDebugLog(`🤖 SleekHear AI found no verses in transcript`);
      }
    } catch (error) {
      addDebugLog(`🤖 SleekHear AI error: ${error.message}`);
      console.error('SleekHear AI Bible detection error:', error);
    }
  };

  detectBiblePassagesRef.current = detectBiblePassages;
  displayPassageByReferenceRef.current = displayPassageByReference;
  detectPassagesWithGroqRef.current = detectPassagesWithGroq;

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

      // Create a fresh SpeechRecognition instance — Edge/Chrome can silently stop
      // processing audio if the same instance is reused across sessions.
      const createRecognition = () => {
        const rec = new SpeechRecognition();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'en-US';
        rec.maxAlternatives = 1;

        rec.onresult = (event) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
              addDebugLog(`📝 Final: "${transcript}"`);
            } else {
              interimTranscript += transcript;
            }
          }

          setInterimText(interimTranscript);

          const displayRef = displayPassageByReferenceRef.current;
          const detectRef = detectBiblePassagesRef.current;
          const detectGroqRef = detectPassagesWithGroqRef.current;

          // Early detection on interim text — don't wait for final transcript
          if (interimTranscript && displayRef) {
            const interimWords = interimTranscript.toLowerCase().split(/\s+/);
            let earlyMatch = null;
            outer: for (let wi = 0; wi < interimWords.length; wi++) {
              for (let wlen = 1; wlen <= Math.min(10, interimWords.length - wi); wlen++) {
                const phrase = interimWords.slice(wi, wi + wlen).join(' ');
                const parsed = parseBibleReference(phrase);
                if (parsed) {
                  if (parsed.verseStart !== null) {
                    earlyMatch = parsed;
                    break outer;
                  } else if (!earlyMatch) {
                    earlyMatch = parsed;
                  }
                }
              }
            }
            if (earlyMatch && earlyMatch.reference !== lastInterimDetectedRef.current) {
              lastInterimDetectedRef.current = earlyMatch.reference;
              addDebugLog(`⚡ Early detect (interim): ${earlyMatch.reference}`);
              displayRef(earlyMatch.reference);
            }
          }

          if (finalTranscript) {
            transcriptBuffer.current += finalTranscript;
            setTranscribedText(transcriptBuffer.current);
            setInterimText('');
            lastInterimDetectedRef.current = '';

            if (detectRef) {
              addDebugLog('🔍 Detecting passages...');
              detectRef(finalTranscript);
              const wordCount = finalTranscript.trim().split(/\s+/).length;
              if (detectGroqRef && wordCount >= 8) {
                detectGroqRef(finalTranscript);
              } else if (wordCount < 8) {
                addDebugLog(`⚠️ SleekHear AI skipped: only ${wordCount} words (need 8+)`);
              }
            }
          }
        };

        rec.onstart = () => {
          isRecognizingRef.current = true;
          setIsRecognizing(true);
          addDebugLog('🎤 Speech recognition STARTED');
        };

        rec.onerror = (event) => {
          addDebugLog(`❌ Error: ${event.error}`);
          isRecognizingRef.current = false;
          setIsRecognizing(false);
          if (event.error === 'not-allowed') {
            toast.error("Microphone access was denied. Allow it in your browser's site settings, then press Start again.");
            isListeningRef.current = false;
            setIsListening(false);
          } else if (event.error === 'aborted') {
            isListeningRef.current = false;
            setIsListening(false);
          }
        };

        rec.onend = () => {
          isRecognizingRef.current = false;
          setIsRecognizing(false);
          addDebugLog('🛑 Recognition ENDED');

          if (isListeningRef.current) {
            addDebugLog('🔄 Auto-restarting recognition (fresh instance)...');
            setTimeout(() => {
              if (!isListeningRef.current) return;
              if (isRecognizingRef.current) {
                addDebugLog('✅ Recognition already running, no restart needed');
                return;
              }
              try {
                // Create a brand-new instance on each restart to avoid Edge stale-mic bug
                recognitionRef.current = createRecognition();
                recognitionRef.current.start();
                addDebugLog('✅ Recognition session restarted (new instance)');
              } catch (e) {
                if (e.name === 'InvalidStateError') return;
                addDebugLog(`❌ Restart failed: ${e.message}`);
                setTimeout(() => {
                  if (!isListeningRef.current || isRecognizingRef.current) return;
                  try {
                    recognitionRef.current = createRecognition();
                    recognitionRef.current.start();
                  } catch (err) {
                    if (err.name !== 'InvalidStateError') {
                      addDebugLog(`❌ Second restart attempt failed: ${err.message}`);
                    }
                  }
                }, 1000);
              }
            }, 150);
          } else {
            addDebugLog('✅ Recognition stopped by user');
          }
        };

        return rec;
      };

      recognitionRef.current = createRecognition();
      addDebugLog('✅ Speech Recognition initialized');
    }

    return () => {
      isListeningRef.current = false;
      if (recognitionRef.current) recognitionRef.current.stop();
    };
    // Mount-only by design: recognition setup must run exactly once; everything
    // it calls goes through refs or stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleListening = () => {
    if (isListening) {
      isListeningRef.current = false;
      setIsListening(false);
      addDebugLog('🛑 Stopping voice capture...');
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      stopAudioMonitoring();
    } else {
      isListeningRef.current = true;
      logActivity('voice_search_started', {
        feature: 'bible_verse_detection'
      });
      setIsListening(true);
      transcriptBuffer.current = '';
      setTranscribedText('');
      setDetectedPassages([]);
      addDebugLog('🎤 Starting voice capture...');

      try {
        if (recognitionRef.current) {
          if (!isRecognizingRef.current) {
            recognitionRef.current.start();
          }
          // else: already running — nothing to do
        }
        startAudioMonitoring();
      } catch (e) {
        if (e.name === 'InvalidStateError') {
          // Already running — treat as success, audio monitoring still starts
          startAudioMonitoring();
        } else {
          addDebugLog(`❌ Failed to start: ${e.message}`);
          isListeningRef.current = false;
          setIsListening(false);
        }
      }
    }
  };

  return {
    isListening,
    isRecognizing,
    transcribedText,
    interimText,
    detectedPassages,
    toggleListening,
  };
}
